import { useEffect, useState } from "react";
import Loader from "../UI/Loader";
import Alert from "../UI/Alert";
import VideoUploaderForm from "./VideoUploaderForm";
import VideoKindTabs from "./VideoKindTabs";
import {
  getMediaTutorialService,
  postMediaTutorialFileService,
  postMediaTutorialUrlService,
  patchMediaTutorialFileService,
  patchMediaTutorialUrlService,
  deleteMediaTutorialService,
} from "../../services/quiz/quiz.service";

const normalizeVideoUrl = (raw) => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${path}`;
};

const extractUrls = (data) => ({
  tutorial: normalizeVideoUrl(
    data?.tutorial?.video_url_generada ||
      data?.tutorial?.video_url_externa ||
      data?.video_url,
  ),
  instrucciones: normalizeVideoUrl(
    data?.instrucciones?.video_url_generada ||
      data?.instrucciones?.video_url_externa,
  ),
});

/**
 * @param {string} tipo - 'inicio_confirmacion' | 'inicio_fortaleza' | 'inicio_aplicacion'
 * @param {string} label - Nombre del módulo para mostrar en UI
 */
const VideoTutorialQuizConfig = ({ tipo, label }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [urls, setUrls] = useState({ tutorial: null, instrucciones: null });
  const [existeRegistro, setExisteRegistro] = useState(false);
  const [activeKind, setActiveKind] = useState("tutorial");

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMediaTutorialService(tipo);
        setUrls(extractUrls(data));
        setExisteRegistro(Boolean(data?.id));
      } catch {
        setExisteRegistro(false);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tipo]);

  const handleSubmit = async ({ file, url }) => {
    setError(null);
    setSuccess(null);
    try {
      setSaving(true);
      let data;

      if (existeRegistro) {
        data = file
          ? await patchMediaTutorialFileService(tipo, file, activeKind)
          : await patchMediaTutorialUrlService(tipo, url, activeKind);
      } else {
        data = file
          ? await postMediaTutorialFileService(tipo, label, file, activeKind)
          : await postMediaTutorialUrlService(tipo, label, url, activeKind);
        setExisteRegistro(true);
      }

      setUrls(extractUrls(data));
      setSuccess(
        activeKind === "instrucciones"
          ? "Video de instrucciones guardado."
          : "Video de bienvenida guardado.",
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Error al guardar el video.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar este video?")) return;
    try {
      setDeleting(true);
      const data = await deleteMediaTutorialService(tipo, activeKind);
      if (data?.message && !data?.id) {
        // Se borró todo el registro (ya no queda ningún video).
        setUrls({ tutorial: null, instrucciones: null });
        setExisteRegistro(false);
      } else {
        setUrls(extractUrls(data));
      }
      setSuccess("Video eliminado.");
    } catch (err) {
      setError(err.response?.data?.error || "Error al eliminar el video.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <Loader center text="Cargando videos..." delay={200} />;
  }

  const currentVideoUrl = urls[activeKind];
  const existeVideo = Boolean(currentVideoUrl);

  return (
    <div>
      {error && <Alert message={error} variant="error" onClose={() => setError(null)} />}
      {success && <Alert message={success} variant="success" onClose={() => setSuccess(null)} />}

      <VideoKindTabs
        activeKind={activeKind}
        onChange={(k) => {
          setActiveKind(k);
          setError(null);
          setSuccess(null);
        }}
        availability={{
          tutorial: Boolean(urls.tutorial),
          instrucciones: Boolean(urls.instrucciones),
        }}
      />

      <VideoUploaderForm
        key={activeKind}
        currentVideoUrl={currentVideoUrl}
        existe={existeVideo}
        saving={saving}
        deleting={deleting}
        onSubmit={handleSubmit}
        onDelete={existeVideo ? handleDelete : undefined}
        submitLabel={existeVideo ? "Actualizar video" : "Guardar video"}
      />
    </div>
  );
};

export default VideoTutorialQuizConfig;
